import React from 'react';
import { Link } from 'react-router-dom';

import { SimpleTooltip } from '@eluve/components';
import { Icon, Separator, Stack } from '@eluve/components';

export interface SummaryGenerationInfoCardProps {
  templateBundle?: { id: string; displayName: string | null } | null;
  promptTemplate?: { id: string; name: string } | null;
  promptVariantNumber?: number | null;
  outputTemplate?: { id: string; name: string } | null;
  outputVariantNumber?: number | null;
  modelArgs?: {
    modelType: string;
    args?: Record<string, unknown> | null;
  } | null;
  startedAt?: string | null;
  completedAt?: string | null;
}

const formatDuration = (ms: number) => {
  if (ms < 1000) return `${ms}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = (seconds % 60).toFixed(0);
  return `${minutes}m ${remainingSeconds}s`;
};

interface TemplateLinkRowProps {
  label: string;
  name: string;
  variantNumber: number | null | undefined;
  toWithVariant: (variant: number) => string;
}

const TemplateLinkRow: React.FC<TemplateLinkRowProps> = ({
  label,
  name,
  variantNumber,
  toWithVariant,
}) => (
  <Stack
    className="flex-1 p-2"
    direction="horizontal"
    align="center"
    gap={2}
    fullWidth={false}
  >
    <SimpleTooltip label={label}>
      <Icon name="FileCode" size="xl" className="mx-1" />
    </SimpleTooltip>
    {variantNumber != null ? (
      <Link to={toWithVariant(variantNumber)}>
        {name} / Variant: {variantNumber}
      </Link>
    ) : (
      <span>{name}</span>
    )}
  </Stack>
);

export const SummaryGenerationInfoCard: React.FC<
  SummaryGenerationInfoCardProps
> = ({
  templateBundle,
  promptTemplate,
  promptVariantNumber,
  outputTemplate,
  outputVariantNumber,
  modelArgs,
  startedAt,
  completedAt,
}) => {
  const durationMs =
    startedAt && completedAt
      ? new Date(completedAt).getTime() - new Date(startedAt).getTime()
      : null;

  return (
    <Stack
      className="rounded-lg border border-border-quaternary text-sm text-text-secondary"
      gap={0}
      align="stretch"
    >
      {templateBundle && (
        <Stack
          className="flex-1 p-2"
          direction="horizontal"
          align="center"
          gap={2}
          fullWidth={false}
        >
          <SimpleTooltip label="Template Bundle">
            <Icon name="Package" size="xl" className="mx-1" />
          </SimpleTooltip>
          <Link to={`/admin/template-bundles/${templateBundle.id}`}>
            {templateBundle.displayName}
          </Link>
        </Stack>
      )}
      {promptTemplate && (
        <TemplateLinkRow
          label="Prompt Template"
          name={promptTemplate.name}
          variantNumber={promptVariantNumber}
          toWithVariant={(v) =>
            `/admin/prompts/${promptTemplate.id}/prompt/variant/${v}`
          }
        />
      )}
      {outputTemplate && (
        <TemplateLinkRow
          label="Output Template"
          name={outputTemplate.name}
          variantNumber={outputVariantNumber}
          toWithVariant={(v) =>
            `/admin/output-templates/${outputTemplate.id}/variant/${v}`
          }
        />
      )}
      {modelArgs && (
        <>
          <Separator className="my-2 ml-12" />
          <Stack
            className="flex-1 p-2"
            direction="horizontal"
            align="start"
            gap={2}
            fullWidth={false}
          >
            <SimpleTooltip label="Model">
              <Icon name="Box" size="xl" className="mx-1" />
            </SimpleTooltip>
            <Stack className="space-y-1" align="stretch">
              <p>{modelArgs.modelType}</p>
              <div>
                Temperature:{' '}
                <b>{String(modelArgs.args?.['temperature'] ?? 'null')}</b>
              </div>
              <div>
                Version: <b>{String(modelArgs.args?.['model'] ?? 'null')}</b>
              </div>
              {durationMs !== null && (
                <div>
                  Duration: <b>{formatDuration(durationMs)}</b>
                </div>
              )}
            </Stack>
          </Stack>
        </>
      )}
    </Stack>
  );
};
